import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FiSearch, FiChevronUp, FiChevronDown, FiPlus, FiCheck, FiUpload } from 'react-icons/fi';
import { CustomerRow } from './CustomerRow';
import { Customer } from '../../../hooks/useCustomers';
import { BatchCheckbox } from '../../../components/BatchCheckbox';
import { InlineBatchActions } from '../../../components/InlineBatchActions';
import { useBatchSelection } from '../../../contexts/BatchSelectionContext';
import { ImportWizardDrawer } from './ImportWizardDrawer';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

interface CustomerTableProps {
  customers: Customer[];
  onEdit: (customer: Customer) => void;
  onView: (customer: Customer) => void;
  onDelete?: (customer: Customer) => void;
  onCreate?: () => void;
  loading?: boolean;
}

type SortField = 'name' | 'email' | 'location' | 'created' | 'balance';
type SortDirection = 'asc' | 'desc';

export const CustomerTable: React.FC<CustomerTableProps> = ({
  customers = [],
  onEdit,
  onView,
  onDelete,
  onCreate,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const itemsPerPage = 10;
  const { token, currentBusiness, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const [balancesByCustomerId, setBalancesByCustomerId] = useState<Record<string, number>>({});
  const [balancesLoading, setBalancesLoading] = useState<boolean>(false);
  const computedRef = useRef<Set<string>>(new Set());
  
  const { 
    selectedItems, 
    selectAll, 
    deselectAll, 
    getSelectedCount,
    setOrderedIds 
  } = useBatchSelection();

  // Precompute balances for all customers once per load; failures default to 0; no repeated retries
  useEffect(() => {
    let mounted = true;
    async function computeAllBalances(targetCustomers: Customer[]) {
      if (!currentBusiness?.id || targetCustomers.length === 0) return;
      setBalancesLoading(true);

      const headers: any = { };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const pending = targetCustomers.filter(c => !computedRef.current.has(c.id));
      const concurrency = Math.min(6, pending.length || 0);
      let index = 0;

      const worker = async () => {
        while (index < pending.length) {
          const c = pending[index++];
          try {
            const [invRes, creditRes] = await Promise.all([
              axios.get(`${API_BASE_URL}/invoices?customer_id=${c.id}&status=open,overdue,partial_open,partial_over`, { headers, withCredentials: isCookieMode }),
              axios.get(`${API_BASE_URL}/payments/customer-credits/${c.id}`, { headers, withCredentials: isCookieMode })
            ]);
            const invoices = Array.isArray(invRes.data?.invoices) ? invRes.data.invoices : invRes.data || [];
            const invoicesBalance = invoices.reduce((sum: number, inv: any) => sum + Number(inv.balance_due || 0), 0);
            const sb = Number(c.starting_balance || 0);
            const credit = Number(creditRes.data?.balance || 0);
            const total = invoicesBalance + sb - credit;
            if (!mounted) return;
            computedRef.current.add(c.id);
            setBalancesByCustomerId(prev => (prev[c.id] === total ? prev : { ...prev, [c.id]: total }));
          } catch (_e) {
            if (!mounted) return;
            computedRef.current.add(c.id);
            setBalancesByCustomerId(prev => (prev[c.id] === 0 ? prev : { ...prev, [c.id]: 0 }));
          }
        }
      };

      await Promise.all(Array.from({ length: Math.max(concurrency, 1) }, worker));
      if (mounted) setBalancesLoading(false);
    }

    // Trigger once when customers list is available; no repeated retries
    if (customers.length) computeAllBalances(customers);
    return () => { mounted = false; };
  }, [customers, currentBusiness?.id]);

  // Handle select all functionality
  const handleSelectAll = () => {
    const visibleCustomerIds = paginatedCustomers.map(customer => customer.id);
    const allVisibleSelected = visibleCustomerIds.every(id => selectedItems.has(id));
    
    if (allVisibleSelected) {
      deselectAll();
    } else {
      selectAll(visibleCustomerIds);
    }
  };

  // Filter customers based on search term (name or address)
  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers;
    const searchLower = searchTerm.toLowerCase();

    return customers.filter(customer => {
      const displayName = (customer.display_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.company_name || '').toLowerCase();
      const emailLower = (customer.email || '').toLowerCase();
      const cityLower = (customer.billing_addr_city || '').toLowerCase();
      const stateLower = (customer.billing_addr_state || '').toLowerCase();
      const streetLower = (customer.billing_addr_line1 || '').toLowerCase();
      const zipLower = (customer.billing_addr_zip || '').toLowerCase();

      return (
        displayName.includes(searchLower) ||
        emailLower.includes(searchLower) ||
        (customer.phone && customer.phone.includes(searchTerm)) ||
        cityLower.includes(searchLower) ||
        stateLower.includes(searchLower) ||
        streetLower.includes(searchLower) ||
        zipLower.includes(searchLower)
      );
    });
  }, [customers, searchTerm]);

  const safeFilteredCustomers = Array.isArray(filteredCustomers) ? filteredCustomers : [];

  // Sort customers
  const sortedCustomers = useMemo(() => {
    return [...safeFilteredCustomers].sort((a, b) => {
      // Name/email/location/created are string sorts; balance is numeric
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = (a.display_name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.company_name || '').toLowerCase();
          bValue = (b.display_name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.company_name || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'location':
          aValue = `${a.billing_addr_city || ''} ${a.billing_addr_state || ''}`.toLowerCase();
          bValue = `${b.billing_addr_city || ''} ${b.billing_addr_state || ''}`.toLowerCase();
          break;
        case 'created':
          aValue = a.created_at || '';
          bValue = b.created_at || '';
          break;
        case 'balance':
          aValue = Number(balancesByCustomerId[a.id] ?? Number(a.starting_balance || 0));
          bValue = Number(balancesByCustomerId[b.id] ?? Number(b.starting_balance || 0));
          break;
        default:
          return 0;
      }

      if (sortField === 'balance') {
        const diff = Number(aValue) - Number(bValue);
        return sortDirection === 'asc' ? diff : -diff;
      }
      return sortDirection === 'asc' 
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [safeFilteredCustomers, sortField, sortDirection, balancesByCustomerId]);

  // Paginate customers
  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedCustomers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedCustomers, currentPage]);

  // Keep ordered ids in sync for shift-click (current page only)
  useEffect(() => {
    setOrderedIds(paginatedCustomers.map(c => c.id));
  }, [paginatedCustomers, setOrderedIds]);

  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <FiChevronUp className="h-3.5 w-3.5 opacity-30" aria-hidden />;
    }
    return sortDirection === 'asc' 
      ? <FiChevronUp className="h-3.5 w-3.5" aria-hidden /> 
      : <FiChevronDown className="h-3.5 w-3.5" aria-hidden />;
  };

  const getSortLabel = (field: SortField) => {
    const currentDirection = sortField === field ? sortDirection : 'none';
    const fieldNames = {
      name: 'Customer Name',
      email: 'Email',
      location: 'Location',
      created: 'Created Date',
      balance: 'Current Balance',
    } as const;
    return `${fieldNames[field]}, ${currentDirection === 'asc' ? 'ascending' : currentDirection === 'desc' ? 'descending' : 'not sorted'}`;
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:bg-neutral-900 dark:border-neutral-800 elev-1">
        <div className="p-6 text-center text-neutral-600" role="status" aria-live="polite">
          Loading customers...
        </div>
        <div className="px-4 sm:px-5 pb-5">
          <ul className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <li key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse dark:bg-neutral-800" />
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:bg-neutral-900 dark:border-neutral-800 elev-1" data-testid="customer-table">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-gray-100 dark:border-neutral-800">
        <div className="relative flex-1 max-w-md">
          <label htmlFor="customer-search" className="sr-only">
            Search customers
          </label>
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
          <input
            id="customer-search"
            type="text"
            placeholder="Search customers by name, email, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
            aria-describedby="search-results"
          />
          <div id="search-results" className="sr-only" aria-live="polite">
            {searchTerm ? `${safeFilteredCustomers.length} customers found` : `${customers.length} total customers`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onCreate && (
            <button
              onClick={onCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
              aria-label="Add new customer"
            >
              <FiPlus className="h-4 w-4" aria-hidden />
              Add Customer
            </button>
          )}
          <button
            onClick={() => setImportWizardOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-sedra bg-white px-4 py-2.5 text-sm font-semibold text-sedra hover:bg-sedra/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900"
            aria-label="Import customers from CSV"
          >
            <FiUpload className="h-4 w-4" aria-hidden />
            Import Customers
          </button>
          {getSelectedCount() > 0 && (
            <span className="hidden sm:inline-flex items-center rounded-full bg-sedra/10 px-2.5 py-1 text-xs font-medium text-sedra">
              {getSelectedCount()} selected
            </span>
          )}
        </div>
      </div>
      
      {/* Selection Indicator */}
      {getSelectedCount() > 0 && (
        <div className="px-4 sm:px-5 pt-3">
          <InlineBatchActions entityType="customers" />
        </div>
      )}

      {/* Table */}
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full" role="table" aria-label="Customer list">
          <thead className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur dark:bg-neutral-800/80">
            <tr>
              {/* Batch Selection Column */}
              <th scope="col" className="px-4 py-3 text-left w-12">
                <div className="flex items-center justify-center">
                  <div 
                    className={`
                      relative inline-flex items-center justify-center w-4 h-4 
                      border rounded cursor-pointer transition-colors
                      ${paginatedCustomers.length > 0 && 
                        paginatedCustomers.every(customer => selectedItems.has(customer.id))
                        ? 'bg-blue-500 border-blue-500' 
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                      }
                      ${paginatedCustomers.length === 0 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:border-blue-400'
                      }
                    `}
                    onClick={handleSelectAll}
                  >
                    {paginatedCustomers.length > 0 && 
                     paginatedCustomers.every(customer => selectedItems.has(customer.id)) && (
                      <FiCheck className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </th>
              <th scope="col" className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                  aria-label={getSortLabel('name')}
                >
                  Customer Name
                  {getSortIcon('name')}
                </button>
              </th>
              <th scope="col" className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('email')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                  aria-label={getSortLabel('email')}
                >
                  Email
                  {getSortIcon('email')}
                </button>
              </th>
              <th scope="col" className="px-4 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  Phone
                </span>
              </th>
              <th scope="col" className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('location')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                  aria-label={getSortLabel('location')}
                >
                  Location
                  {getSortIcon('location')}
                </button>
              </th>
              <th scope="col" className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('created')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                  aria-label={getSortLabel('created')}
                >
                  Created
                  {getSortIcon('created')}
                </button>
              </th>
              <th scope="col" className="px-4 py-3 text-left">
                <span className="sr-only">Actions</span>
              </th>
              {/* Current Balance (rightmost) */}
              <th scope="col" className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('balance')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                  aria-label={getSortLabel('balance')}
                >
                  Current Balance
                  {getSortIcon('balance')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8">
                  <div className="p-10 text-center">
                    <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-sedra/10 text-sedra mb-3">✺</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  onEdit={onEdit}
                  onView={onView}
                  onDelete={onDelete}
                  currentBalance={balancesByCustomerId[customer.id]}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-t border-gray-100 dark:border-neutral-800">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedCustomers.length)} of {sortedCustomers.length} customers
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
              aria-label="Previous page"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Import Wizard Drawer */}
      <ImportWizardDrawer
        isOpen={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        onComplete={() => {
          setImportWizardOpen(false);
          // Optionally refresh the customer list
          window.location.reload();
        }}
      />
    </div>
  );
}; 